import * as React from 'react';

import {
  createStyles,
  Theme,
  Typography,
  withStyles,
  WithStyles,
} from '@nelnet/unifi-components-react';

import Link from './Link';
import usePageData from '../hooks/usePageData';

const styles = ({ spacing, breakpoints }: Theme) =>
  createStyles({
    breadcrumbs: {
      margin: `0 ${spacing(2)}px 0 0`,
      padding: 0,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      maxWidth: 'calc(100vw - 120px)',
      [breakpoints.up('md')]: {
        marginLeft: spacing(4),
      },
      '& a:visited': {
        color: 'inherit',
      },
      '& li': {
        listStyle: 'none',
      },
      '& li, & li *': {
        lineHeight: '1.5',
        color: 'inherit',
      },
      '& li + li': {
        marginLeft: spacing(1),
      },
      '& li + li:before': {
        content: '" / "',
        display: 'inline-block',
        marginRight: spacing(1),
      },
    },
  });

interface IBreadcrumbsProps extends WithStyles<typeof styles> {
  path: string;
  title: string;
}

const navPrimary = [
  {
    title: 'Get Started',
    slug: 'getstarted',
  },
  {
    title: 'Theming',
    slug: 'theming',
  },
  {
    title: 'Changelog',
    slug: 'changelog',
  },
  {
    title: 'Contact Us',
    slug: 'contact',
  },
  {
    title: 'FAQs',
    slug: 'faqs',
  },
];

/*
Creates "crumb" segments based on the current url "path", with a few conditions unique to component pages
*/
const Breadcrumbs: React.FunctionComponent<IBreadcrumbsProps> = ({ path, title, classes }) => {
  if (path === '') {
    return <></>;
  }

  if (path.includes('draft')) {
    return <>Draft: {title}</>;
  }

  const currPath = path.split('/');
  const crumbPath = currPath.filter((crumb) => crumb);
  const isComponent = ['react', 'web'].includes(crumbPath[0]);

  const crumbs = crumbPath.map((segment, i) => {
    const useSlug = i === crumbPath.length - 1 ? false : segment;
    let useTitle = title;
    let permalink = '';
    const isComponentPlatform = isComponent && i === 0;
    const isComponentPage = isComponent && i === 1;

    // No page exists for just /react/ or /web/
    // so exclude from crumbs
    if (isComponentPlatform) {
      return null;
    }

    if (useSlug && !isComponentPage) {
      const primaryNavItem = navPrimary.find((page) => page.slug === segment);

      if (primaryNavItem) {
        useTitle = primaryNavItem.title;
        permalink = primaryNavItem.slug;
      } else {
        permalink = crumbPath
          .filter((_e, index) => {
            return index <= i;
          })
          .join('/');

        const crumbTitle = usePageData({ type: 'single', slug: `/${permalink}/` });
        useTitle = crumbTitle.title;
      }
    }

    if (isComponentPage) {
      const platform = crumbPath[0];
      const platformTitle = platform.charAt(0).toUpperCase() + platform.slice(1);
      useTitle = `${platformTitle} Components`;
      permalink = `${platform}/components/`;
    }

    const crumb = useSlug ? <Link href={`/${permalink}`}>{useTitle}</Link> : <>{useTitle}</>;

    return <li key={segment}>{crumb}</li>;
  });

  return (
    <Typography component="ul" className={classes.breadcrumbs}>
      {crumbs}
    </Typography>
  );
};

export default withStyles(styles)(Breadcrumbs);
